// Shared icon set - review item 7 (ENKO_DESIGN_REVIEW.html).
//
// ONE spec for every icon in the app, matching the review's reference implementation exactly:
//   16px x 16px, viewBox 0 0 24 24, stroke-width 1.75, round caps and joins,
//   fill: none, stroke: currentColor
//
// `currentColor` is the load-bearing part: an icon inherits its parent's text colour, so the
// same component renders correctly on a light card, on the navy active nav item (where the
// parent sets white or gold), and in dark mode, with no per-context variant.
//
// WHY A SHARED MODULE RATHER THAN PER-FILE INLINE SVG
// The review counted 31 inline SVGs across 6 files with 3 different stroke-widths (1.8, 2.0,
// 2.2) and no shared size. Dashboard's own 11 were internally consistent at 15px/1.8, but every
// other file re-declared its own. Defining them once here means the remaining shells adopt the
// same set in Stage 2 by importing rather than by copying - which is the specific failure mode
// that produced the drift in the first place.
//
// Geometry is preserved verbatim from Dashboard.js's originals: only the wrapper attributes
// (size and stroke-width) change, so every icon still reads as exactly what it read as before.

import React from 'react';

/** The single wrapper every icon uses. `size` exists only for the rare case a call site needs a
 *  larger glyph; it defaults to the approved 16px and should almost never be passed. */
const Svg = ({ size = 16, children, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {children}
  </svg>
);

/** Oversight / dashboard: four panes. */
export const IconDashboard = (p) => (
  <Svg {...p}>
    <rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" />
    <rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" />
  </Svg>
);

/** A single person: My Profile. */
export const IconUser = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="7" r="4" /><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
  </Svg>
);

/** Two people: the employee roster. */
export const IconUsers = (p) => (
  <Svg {...p}>
    <circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0-3-3.85" />
  </Svg>
);

/** Person with a plus: New starters. */
export const IconUserPlus = (p) => (
  <Svg {...p}>
    <circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    <line x1="19" y1="8" x2="19" y2="14" /><line x1="16" y1="11" x2="22" y2="11" />
  </Svg>
);

/** Person with a minus: Leavers. */
export const IconUserMinus = (p) => (
  <Svg {...p}>
    <circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    <line x1="16" y1="11" x2="22" y2="11" />
  </Svg>
);

/** Shield with a tick: Compliance, Policies, Retention, Grievances. */
export const IconShield = (p) => (
  <Svg {...p}>
    <path d="M9 12l2 2 4-4" />
    <path d="M12 3a12 12 0 0 0 8.5 3 12 12 0 0 1-8.5 15A12 12 0 0 1 3.5 6 12 12 0 0 0 12 3" />
  </Svg>
);

/** Stacked documents: Workflows, Policies. */
export const IconFiles = (p) => (
  <Svg {...p}>
    <path d="M15 3v4a1 1 0 0 0 1 1h4" />
    <path d="M18 17h-7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4l5 5v7a2 2 0 0 1-2 2z" />
    <path d="M16 17v2a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" />
  </Svg>
);

/** Bar chart: Analytics, HR Report, Compliance Report. */
export const IconChart = (p) => (
  <Svg {...p}>
    <rect x="3" y="12" width="4" height="9" rx="1" /><rect x="9" y="8" width="4" height="13" rx="1" />
    <rect x="15" y="4" width="4" height="17" rx="1" /><line x1="3" y1="21" x2="21" y2="21" />
  </Svg>
);

/** Wrench: App requests. */
export const IconTools = (p) => (
  <Svg {...p}>
    <path d="M3 21h4L20 8a1.5 1.5 0 0 0-4-4L3 17v4" /><line x1="14.5" y1="5.5" x2="18.5" y2="9.5" />
  </Svg>
);

/** Circled tick: Tasks, Validation. */
export const IconCheck = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" /><polyline points="9 12 11 14 15 10" />
  </Svg>
);

/** Trend line: Performance. */
export const IconTrend = (p) => (
  <Svg {...p}>
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
  </Svg>
);

/** Warning triangle with exclamation. EMOJICLEAN: replaces the warning emoji glyphs. */
export const IconWarning = (p) => (
  <Svg {...p}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </Svg>
);

/** Gear: settings / maintenance. EMOJICLEAN: replaces the gear emoji. */
export const IconSettings = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </Svg>
);

/** Sun: light-mode indicator on the dark-mode toggle. EMOJICLEAN: replaces the sun emoji. */
export const IconSun = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </Svg>
);

/** Moon: dark-mode indicator on the dark-mode toggle. EMOJICLEAN: replaces the moon emoji. */
export const IconMoon = (p) => (
  <Svg {...p}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </Svg>
);
